import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Bookmark, Plus, Copy, Check } from 'lucide-react';
import Header from '../components/Header';
import SEOHead from '../components/SEOHead';
import FavouriteButton from '../components/FavouriteButton';
import InspoSignInModal from '../components/InspoSignInModal';
import { useGuestIdentity } from '../utils/useGuestIdentity';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * /inspo/saved — visitor's saved boards + the flat "All Saved" view.
 *
 * Shows TWO sections:
 *  1. "All Saved" — every hearted item resolved into a masonry grid. Works
 *     for unregistered guests too (uses localStorage faves).
 *  2. "My Lists" — explicit boards the user has created. Requires sign-in.
 */
const InspoSavedPage = () => {
  const { isRegistered, token, faves } = useGuestIdentity();
  const [resolvedItems, setResolvedItems] = useState(null); // null = loading
  const [lists, setLists] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  // Resolve flat faves -> displayable items (works for both signed-in + guest)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!faves || faves.length === 0) {
        if (mounted) setResolvedItems([]);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/inspo/items-by-refs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(faves.map(f => ({ item_type: f.item_type, item_id: f.item_id }))),
        });
        const d = res.ok ? await res.json() : { items: [] };
        if (mounted) setResolvedItems(d.items || []);
      } catch {
        if (mounted) setResolvedItems([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [faves]);

  // Load user's explicit lists (only if registered)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!isRegistered) {
        if (mounted) setLists([]);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/inspo-lists/mine`, { headers: { Authorization: `Bearer ${token}` } });
        const d = res.ok ? await res.json() : [];
        if (mounted) setLists(Array.isArray(d) ? d : []);
      } catch {
        if (mounted) setLists([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [isRegistered, token]);

  const createList = async () => {
    if (!isRegistered) { setShowSignIn(true); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/inspo-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          // Pre-populate the list with everything currently hearted
          item_refs: (faves || []).map(f => ({ item_type: f.item_type, item_id: f.item_id })),
        }),
      });
      if (!res.ok) throw new Error('Could not create list');
      const newList = await res.json();
      navigate(`/inspo/list/${newList.slug}`);
    } catch (e) { /* silent */ }
    finally { setCreating(false); }
  };

  const copyShare = async (slug) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/inspo/list/${slug}`);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(''), 1800);
    } catch { /* ignore */ }
  };

  const totalSaved = (faves || []).length;

  return (
    <div className="bg-white text-black min-h-screen" data-testid="inspo-saved-page">
      <SEOHead title="Saved Inspo — Crystal Media" description="Your private inspo boards." url="/inspo/saved" />
      <Header variant="light" />

      <section className="pt-28 md:pt-32 pb-6 px-6 max-w-5xl mx-auto">
        <Link to="/inspo" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-black/40 hover:text-black mb-6 tap-target" data-testid="inspo-saved-back">
          <ArrowLeft size={13} /> Back to feed
        </Link>
        <h1 className="font-presti text-4xl md:text-5xl tracking-[0.02em] leading-[1.05] mb-3 lowercase">your inspo</h1>
        <p className="text-black/55 text-sm md:text-base">
          {totalSaved > 0
            ? <>You&rsquo;ve saved <strong className="text-black" data-testid="inspo-saved-count">{totalSaved}</strong> piece{totalSaved === 1 ? '' : 's'} of inspiration.</>
            : <>Tap any heart icon across the site to start collecting.</>}
        </p>
      </section>

      {/* ─── ALL SAVED — flat hearted items ─── */}
      <section className="px-3 md:px-6 pb-12 max-w-5xl mx-auto" data-testid="inspo-saved-all">
        {resolvedItems === null ? (
          <div className="py-12 text-center"><Loader2 size={22} className="animate-spin inline text-black/30" /></div>
        ) : resolvedItems.length === 0 && totalSaved === 0 ? (
          null
        ) : resolvedItems.length === 0 && totalSaved > 0 ? (
          <div className="border border-black/10 p-8 text-center mx-3 md:mx-0">
            <p className="text-sm text-black/55">Your saved items are loading…</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-3 md:px-0 mb-4">
              <p className="text-[10px] tracking-[0.25em] uppercase text-black/55">All saved</p>
              {isRegistered && resolvedItems.length > 0 && (
                <button
                  type="button"
                  onClick={createList}
                  disabled={creating}
                  data-testid="inspo-saved-make-list"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-black/55 hover:text-black tap-target"
                >
                  {creating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                  Save all to a list
                </button>
              )}
            </div>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-2 md:gap-3" data-testid="inspo-saved-grid">
              {resolvedItems.map(item => (
                <SavedCard key={`${item.kind}:${item.id}`} item={item} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* ─── EXPLICIT LISTS ─── */}
      <section className="px-6 pb-24 max-w-3xl mx-auto" data-testid="inspo-saved-lists">
        <p className="text-[10px] tracking-[0.25em] uppercase text-black/55 mb-4">My lists</p>

        {lists === null ? (
          <div className="py-8 text-center"><Loader2 size={18} className="animate-spin inline text-black/30" /></div>
        ) : !isRegistered ? (
          <div className="border border-black/10 p-8 text-center">
            <Bookmark size={22} className="mx-auto mb-3 text-black/30" />
            <p className="text-sm text-black/65 mb-1">Sign in to organize your saves into shareable lists.</p>
            <p className="text-xs text-black/40 mb-5">We&rsquo;ll keep everything you&rsquo;ve already hearted.</p>
            <button
              type="button"
              onClick={() => setShowSignIn(true)}
              data-testid="inspo-saved-signin"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-black/85 transition-colors"
            >
              Sign in to organize
            </button>
          </div>
        ) : lists.length === 0 ? (
          <div className="border border-black/10 p-8 text-center">
            <Bookmark size={22} className="mx-auto mb-3 text-black/30" />
            <p className="text-sm text-black/55 mb-4">No custom lists yet.</p>
            <button
              type="button"
              onClick={createList}
              disabled={creating}
              data-testid="inspo-saved-create-empty"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-black/85 transition-colors disabled:bg-black/40"
            >
              {creating ? <><Loader2 size={13} className="animate-spin" /> Creating…</> : <><Plus size={13} /> Make a list from saves</>}
            </button>
          </div>
        ) : (
          <>
            <ul className="space-y-3">
              {lists.map(l => {
                const shareUrl = `${window.location.origin}/inspo/list/${l.slug}`;
                const isCopied = copiedSlug === l.slug;
                return (
                  <li key={l.slug} className="border border-black/10 hover:border-black/30 transition-colors" data-testid={`inspo-saved-list-${l.slug}`}>
                    <Link to={`/inspo/list/${l.slug}`} className="block px-5 py-4 hover:bg-black/[0.02] transition-colors">
                      <p className="font-medium text-sm">{l.name || 'Untitled list'}</p>
                      <p className="text-[11px] text-black/45 mt-0.5">{(l.item_refs || []).length} saved</p>
                    </Link>
                    {/* Share row — prominent text link + copy button */}
                    <div className="flex items-stretch border-t border-black/5 bg-black/[0.015]">
                      <div className="flex-1 px-5 py-3 min-w-0">
                        <p className="text-[9px] tracking-[0.25em] uppercase text-black/40 mb-1">Share this list</p>
                        <p className="text-xs text-black/70 font-mono truncate" data-testid={`inspo-saved-share-url-${l.slug}`}>{shareUrl}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyShare(l.slug)}
                        data-testid={`inspo-saved-share-${l.slug}`}
                        aria-label={isCopied ? 'Copied' : 'Copy share link'}
                        className={`min-w-[100px] px-4 border-l border-black/10 inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-medium transition-colors ${
                          isCopied ? 'bg-black text-white' : 'text-black/70 hover:bg-black hover:text-white'
                        }`}
                      >
                        {isCopied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              onClick={createList}
              disabled={creating}
              data-testid="inspo-saved-create"
              className="w-full mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 border border-black/15 text-[10px] tracking-[0.2em] uppercase font-medium text-black/70 hover:border-black hover:text-black transition-colors disabled:opacity-50"
            >
              {creating ? <><Loader2 size={13} className="animate-spin" /> Creating…</> : <><Plus size={13} /> New list</>}
            </button>
          </>
        )}
      </section>

      <InspoSignInModal
        isOpen={showSignIn}
        onClose={() => setShowSignIn(false)}
        onSuccess={() => setShowSignIn(false)}
        headline="Sign in to save"
      />
    </div>
  );
};

/* ─────────────── Saved card (masonry tile) ─────────────── */
const SavedCard = ({ item }) => {
  const isVideo = item.media_type === 'video';
  const isGif = item.media_type === 'gif';
  const link = item.kind === 'photo' && item.collection_id
    ? `/collection/${item.collection_id}`
    : item.kind === 'film'
      ? '/film/theatre'
      : '/inspo';
  return (
    <div className="mb-2 md:mb-3 break-inside-avoid relative group" data-testid={`saved-card-${item.kind}-${item.id}`}>
      <Link to={link} className="block relative overflow-hidden bg-black/5">
        {isVideo ? (
          <video
            src={item.media_url}
            poster={item.thumbnail_url || undefined}
            className="w-full h-auto block"
            muted loop playsInline preload="metadata"
          />
        ) : (
          <img
            src={item.media_url}
            alt={item.caption || ''}
            className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
            loading="lazy"
          />
        )}
        {(isVideo || isGif) && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/65 text-white text-[9px] tracking-[0.2em] uppercase">{isVideo ? 'Film' : 'GIF'}</span>
        )}
      </Link>
      <div className="absolute top-2 right-2 z-10">
        <FavouriteButton itemType={item.kind} itemId={item.id} variant="light" size={14} />
      </div>
    </div>
  );
};

export default InspoSavedPage;
